import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaBars, FaTimes, FaChevronDown, FaChevronRight, FaSeedling, FaLeaf, FaSpa } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productData } from '../../data/ProductData';
import { WHATSAPP_NUMBER } from '../../data/contact';

// Importar todas las imágenes
import sorgoForrajeroColiqueo from '../../assets/products/sorgo-forrajero-sensitivo-coliqueo.jpeg';
import sorgoGraniferoTucan from '../../assets/products/sorgo-granifero-tucan.jpeg';
import sorgoSileroYanquetruz from '../../assets/products/sorgo-silero-azucarado-yanquetruz.jpeg';
import sorgoDoblePropositoSantaAna from '../../assets/products/sorgo-doble-proposito-santa-ana.jpeg';
import sorgoDoblePropositoSantaLucia from '../../assets/products/sorgo-doble-proposito-santa-lucia.jpeg';
import festucaRizomat from '../../assets/products/festuca-rizomat.jpeg';
import ryegrassPerenne from '../../assets/products/ryegrass-perenne.jpg';
import ryegrassPerenneNui from '../../assets/products/ryegrass-perenne-nui.jpeg';
import semillaLotus from '../../assets/products/semilla-lotus.jpg';
import lotusTenuis from '../../assets/products/lotus-tenuis.jpeg';
import semillaMoha from '../../assets/products/semilla-moha.webp';
import mohaGiganteColorada from '../../assets/products/moha-gigante-colorada.jpeg';
import avenaIntaSurena from '../../assets/products/avena-inta-sureña.jpg';
import ryegrassPerenneTurftype from '../../assets/products/ryegrass-perenne-turftype.jpeg';
import semillasRyegrassTurftype from '../../assets/products/semillas-ryegrass-turftype-ornamentales.jpg';
import blendFestucaRyegrassSantaLucia from '../../assets/products/blend-festuca-ryegrass-santa-lucia.jpg';

// Función para generar imagen placeholder
const generatePlaceholderImage = (seedType, title) => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    
    // Colores según el tipo de semilla (soporta múltiples idiomas)
    const colors = {
        'Sorgos': { bg: '#8B4513', text: '#FFFFFF' },
        'Sorghums': { bg: '#8B4513', text: '#FFFFFF' },
        '高粱': { bg: '#8B4513', text: '#FFFFFF' },
        'Semillas Forrajeras': { bg: '#7acc62', text: '#FFFFFF' },
        'Forage Seeds': { bg: '#7acc62', text: '#FFFFFF' },
        '牧草種子': { bg: '#7acc62', text: '#FFFFFF' },
        'Semillas Ornamentales': { bg: '#18776a', text: '#FFFFFF' },
        'Ornamental Seeds': { bg: '#18776a', text: '#FFFFFF' },
        '觀賞種子': { bg: '#18776a', text: '#FFFFFF' }
    };
    
    const color = colors[seedType] || { bg: '#808080', text: '#FFFFFF' };
    
    // Fondo
    ctx.fillStyle = color.bg;
    ctx.fillRect(0, 0, 400, 300);
    
    // Texto
    ctx.fillStyle = color.text;
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    
    // Dividir el título en líneas
    const words = title.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
        const testLine = currentLine + word + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > 350 && currentLine !== '') {
            lines.push(currentLine.trim());
            currentLine = word + ' ';
        } else {
            currentLine = testLine;
        }
    });
    lines.push(currentLine.trim());
    
    // Dibujar las líneas
    const lineHeight = 20;
    const startY = 150 - (lines.length * lineHeight) / 2;
    lines.forEach((line, index) => {
        ctx.fillText(line, 200, startY + (index * lineHeight));
    });
    
    // Tipo de semilla
    ctx.font = '12px Arial';
    ctx.fillText(seedType, 200, startY + (lines.length * lineHeight) + 20);
    
    return canvas.toDataURL();
};

// Sistema de múltiples imágenes por producto
const productImages = {
    0: { // Sorgo Forrajero Fotosensitivo Coliqueo
        main: sorgoForrajeroColiqueo,
        seed: null,
        growth: null
    },
    1: { // Sorgo Granífero Tucán
        main: sorgoGraniferoTucan,
        seed: null,
        growth: null
    },
    2: { // Sorgo Silero Azucarado Yanquetruz
        main: sorgoSileroYanquetruz,
        seed: null,
        growth: null
    },
    3: { // Sorgo Doble Propósito Santa Ana
        main: sorgoDoblePropositoSantaAna,
        seed: null,
        growth: null
    },
    4: { // Sorgo Doble Propósito Santa Lucía
        main: sorgoDoblePropositoSantaLucia,
        seed: null,
        growth: null
    },
    5: { // Festuca Rizomat
        main: festucaRizomat,
        seed: null,
        growth: null
    },
    6: { // Ryegrass Perenne Forrajero NUI
        main: ryegrassPerenneNui,
        seed: ryegrassPerenne, // Imagen de semilla
        growth: ryegrassPerenneNui // Imagen de crecimiento
    },
    7: { // Blend Festuca con Ryegrass Santa Lucía
        main: blendFestucaRyegrassSantaLucia,
        seed: null,
        growth: null
    },
    8: { // Lotus Tenuis
        main: lotusTenuis,
        seed: semillaLotus, // Imagen de semilla
        growth: lotusTenuis // Imagen de crecimiento
    },
    9: { // Moha Gigante Colorada
        main: mohaGiganteColorada,
        seed: semillaMoha, // Imagen de semilla
        growth: mohaGiganteColorada // Imagen de crecimiento
    },
    10: { // Avena INTA Sureña
        main: avenaIntaSurena,
        seed: null,
        growth: null
    },
    11: { // Ryegrass Perenne Turftype Don Luis
        main: ryegrassPerenneTurftype,
        seed: semillasRyegrassTurftype, // Imagen de semilla
        growth: ryegrassPerenneTurftype // Imagen de crecimiento
    }
};

// Array simple para compatibilidad con Products.js
const images = [
    sorgoForrajeroColiqueo,    // Sorgo Forrajero Fotosensitivo Coliqueo (0)
    sorgoGraniferoTucan,       // Sorgo Granífero Tucán (1)
    sorgoSileroYanquetruz,     // Sorgo Silero Azucarado Yanquetruz (2)
    sorgoDoblePropositoSantaAna,    // Sorgo Doble Propósito Santa Ana (3)
    sorgoDoblePropositoSantaLucia,   // Sorgo Doble Propósito Santa Lucía (4)
    festucaRizomat,           // Festuca Rizomat (5)
    ryegrassPerenneNui,       // Ryegrass Perenne Forrajero NUI (6) - mejor calidad
    blendFestucaRyegrassSantaLucia, // Blend Festuca con Ryegrass Santa Lucía (7)
    lotusTenuis,              // Lotus Tenuis (8) - mejor calidad
    mohaGiganteColorada,      // Moha Gigante Colorada (9) - mejor calidad
    avenaIntaSurena,          // Avena INTA Sureña (10)
    ryegrassPerenneTurftype,  // Ryegrass Perenne Turftype Don Luis (11)
];

// Styled Components
const Container = styled.div`
    display: flex;
    height: 100vh; /* Altura completa de la ventana */
    background: linear-gradient(135deg, #e1e4d5 0%, #c3cfe2 100%);
    padding-top: 60px; /* Espacio para la navbar */
    overflow: hidden; /* Evita el scroll del contenedor principal */
    
    @media screen and (max-width: 768px) {
        flex-direction: column;
        padding-top: 70px; /* Navbar más pequeña en móviles */
        overflow: auto; /* Permite scroll en móviles */
    }
`;

const Sidebar = styled.div`
    width: 300px;
    background: #066a67;
    padding: 2rem 0;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    height: calc(100vh - 60px); /* Altura fija igual al contenedor */
    overflow-y: auto; /* Solo la sidebar hace scroll si es necesario */
    position: sticky;
    top: 60px; /* Se mantiene pegada debajo de la navbar */
    
    @media screen and (max-width: 768px) {
        position: fixed;
        top: 0;
        left: ${props => props.isOpen ? '0' : '-100%'};
        width: 280px;
        height: 100vh;
        z-index: 1000;
        transition: left 0.3s ease;
        padding-top: 4rem;
        box-shadow: ${props => props.isOpen ? '2px 0 10px rgba(0, 0, 0, 0.3)' : 'none'};
    }
`;

const MobileMenuButton = styled.button`
    display: none;
`;

const MobileOverlay = styled.div`
    display: none;
    
    @media screen and (max-width: 768px) {
        display: ${props => props.isOpen ? 'block' : 'none'};
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        z-index: 999;
    }
`;

const SidebarTitle = styled.h2`
    color: white;
    text-align: center;
    margin-bottom: 2rem;
    font-size: 1.5rem;
    font-weight: 600;
`;

const ProductListItem = styled.div`
    padding: 1rem 1.5rem;
    color: ${props => props.isActive ? '#066a67' : '#e1e4d5'};
    background: ${props => props.isActive ? '#e1e4d5' : 'transparent'};
    cursor: pointer;
    transition: all 0.3s ease;
    border-left: ${props => props.isActive ? '4px solid #18776a' : '4px solid transparent'};
    
    &:hover {
        background: ${props => props.isActive ? '#e1e4d5' : '#066a67'};
        color: ${props => props.isActive ? '#066a67' : 'white'};
    }
`;

const ProductName = styled.div`
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 0.5rem;
`;

const ProductType = styled.div`
    font-size: 0.85rem;
    opacity: 0.8;
`;

const CategoryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: ${props => props.isExpanded ? '#066a67' : '#066a67'};
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;
    border-left: 4px solid ${props => props.categoryColor || '#18776a'};
    
    &:hover {
        background: #066a67;
    }
`;

const CategoryTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    font-weight: 600;
    font-size: 1rem;
`;

const CategoryIcon = styled.div`
    font-size: 1.1rem;
    color: ${props => props.categoryColor || '#18776a'};
`;

const CategoryChevron = styled.div`
    font-size: 0.9rem;
    transition: transform 0.3s ease;
    transform: ${props => props.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
`;

const CategoryContent = styled.div`
    max-height: ${props => props.isExpanded ? '1000px' : '0'};
    overflow: hidden;
    transition: max-height 0.3s ease;
`;

const CategoryProductList = styled.div`
    background: #066a67;
`;

const DetailArea = styled.div`
    flex: 1;
    padding: 2rem;
    overflow-y: auto;
    height: calc(100vh - 80px); /* Altura fija igual al contenedor */
    
    @media screen and (max-width: 768px) {
        width: 100%;
        height: calc(100vh - 70px);
        padding: 1rem;
        padding-bottom: 100px; /* Espacio para el botón flotante */
    }
`;

const ProductDetailContent = styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 1rem;
    gap: 2rem;
    align-items: flex-start;
    
    @media screen and (max-width: 768px) {
        flex-direction: column;
        padding: 0 0.5rem;
        gap: 1rem;
        align-items: center;
    }
`;

const ImageColumn = styled.div`
    flex: 0 0 400px;
    display: flex;
    justify-content: center;
    
    @media screen and (max-width: 768px) {
        flex: none;
        width: 100%;
    }
`;

const ContentColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    
    @media screen and (max-width: 768px) {
        width: 100%;
    }
`;

const ImageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: 380px;
    min-height: ${props => props.isSorgo ? '320px' : 'auto'};
    
    @media screen and (max-width: 768px) {
        max-width: 300px;
        min-height: ${props => props.isSorgo ? '270px' : 'auto'};
    }
`;

const ProductImage = styled.img`
    width: 100%;
    height: ${props => props.isSorgo ? '300px' : '200px'};
    object-fit: cover;
    border-radius: 10px;
    flex-shrink: 0;
    cursor: pointer;
    transition: transform 0.3s ease;
    
    &:hover {
        transform: scale(1.02);
    }
    
    @media screen and (max-width: 768px) {
        height: ${props => props.isSorgo ? '250px' : '150px'};
        margin-bottom: 0.5rem;
    }
`;

const ImageLabel = styled.div`
    font-size: 0.9rem;
    font-weight: 600;
    color: #066a67;
    text-align: center;
    margin-bottom: 0.5rem;
    
    @media screen and (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const ImageModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.9);
    display: ${props => props.isOpen ? 'flex' : 'none'};
    justify-content: center;
    align-items: center;
    z-index: 2000;
    cursor: pointer;
`;

const ModalImage = styled.img`
    max-width: 90%;
    max-height: 90%;
    object-fit: contain;
    border-radius: 10px;
`;

const ModalCloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    font-size: 2rem;
    cursor: pointer;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }
`;

const ProductTitle = styled.h1`
    color: #066a67;
    font-size: 2rem;
    margin-bottom: 1rem;
    text-align: left;
    flex-shrink: 0;
    
    @media screen and (max-width: 768px) {
        font-size: 1.6rem;
        margin-bottom: 0.8rem;
        text-align: center;
    }
`;

const ProductTypeTag = styled.span`
    background: #18776a;
    color: white;
    padding: 0.4rem 0.8rem;
    border-radius: 10px;
    font-size: 0.85rem;
    display: inline-block;
    margin-bottom: 1rem;
    flex-shrink: 0;
`;

const ProductPrice = styled.div`
    font-size: 1.3rem;
    font-weight: 600;
    color: #7acc62;
    margin-bottom: 1.5rem;
    text-align: left;
    flex-shrink: 0;
    
    @media screen and (max-width: 768px) {
        text-align: center;
        font-size: 1.1rem;
        margin-bottom: 1rem;
    }
`;

const ConsultPriceButton = styled(motion.button)`
    position: fixed;
    bottom: 40px;
    right: 40px;
    background: linear-gradient(135deg, #18776a 0%, #066a67 100%);
    color: white;
    border: none;
    padding: 1rem 1.5rem;
    border-radius: 50px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    // transition: transform 0.3s ease, box-shadow 0.3s ease;   
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
    z-index: 1000;
    white-space: nowrap;
    
    &:hover {
        transform: translateY(-2px);
        // box-shadow: 0 10px 25px rgba(52, 152, 219, 0.3);
    }
    
    &:active {
        // transform: translateY(0);
    }
    
    @media screen and (max-width: 768px) {
        bottom: 30px;
        right: 20px;
        padding: 0.8rem 1.2rem;
        font-size: 0.9rem;
        border-radius: 40px;
        z-index: 999;
    }
    
    @media screen and (max-width: 480px) {
        bottom: 25px;
        right: 15px;
        padding: 0.7rem 1rem;
        font-size: 0.85rem;
        border-radius: 35px;
    }
`;

const SpecificationsContainer = styled.div`
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;
    flex-shrink: 0;
`;

const SpecificationsTitle = styled.h4`
    color: #066a67;
    font-size: 1.1rem;
    margin-bottom: 0.8rem;
    text-align: left;
    
    @media screen and (max-width: 768px) {
        text-align: center;
        font-size: 1rem;
    }
`;

const SpecificationItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.3rem 0;
    border-bottom: 1px solid #e9ecef;
    font-size: 0.9rem;
    
    &:last-child {
        border-bottom: none;
    }
    
    @media screen and (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.2rem;
    }
`;

const SpecLabel = styled.span`
    font-weight: 600;
    color: #495057;
`;

const SpecValue = styled.span`
    color: #6c757d;
    
    @media screen and (max-width: 768px) {
        font-size: 0.85rem;
    }
`;

const FeaturesTitle = styled.h3`
    color: #066a67;
    font-size: 1.4rem;
    margin-bottom: 1rem;
    flex-shrink: 0;
    text-align: left;
    
    @media screen and (max-width: 768px) {
        font-size: 1.2rem;
        margin-bottom: 0.8rem;
        text-align: center;
    }
`;

const FeaturesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    flex: 1;
    overflow-y: auto;
    max-height: calc(100vh - 300px);
    
    @media screen and (max-width: 768px) {
        max-height: calc(100vh - 400px);
    }
`;

const FeatureItem = styled.li`
    padding: 0.8rem 0;
    border-bottom: 1px solid #e1e4d5;
    position: relative;
    padding-left: 2rem;
    font-size: 1rem;
    line-height: 1.5;
    color: #066a67;
    
    &:before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #7acc62;
        font-weight: bold;
        font-size: 1.2rem;
    }
    
    &:last-child {
        border-bottom: none;
    }
    
    @media screen and (max-width: 768px) {
        padding: 0.6rem 0;
        font-size: 0.9rem;
        padding-left: 1.8rem;
    }
`;

const WelcomeMessage = styled.div`
    text-align: center;
    color: #7f8c8d;
    font-size: 1.2rem;
    margin-top: 3rem;
`;

const ProductDetail = () => {
    const { t } = useTranslation();
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [expandedCategories, setExpandedCategories] = useState({
        'Sorgos': true,
        'Semillas Forrajeras': true,
        'Semillas Ornamentales': true
    });
    const [modalImage, setModalImage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Agrupar productos por categoría
    const groupedProducts = productData.reduce((acc, product, index) => {
        const category = product.type;
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push({ ...product, originalIndex: index });
        return acc;
    }, {});

    // Configuración de iconos y colores por categoría
    const categoryConfig = {
        'Sorgos': { icon: FaSeedling, color: '#8B4513' },
        'Semillas Forrajeras': { icon: FaLeaf, color: '#7acc62' },
        'Semillas Ornamentales': { icon: FaSpa, color: '#18776a' }
    };

    // Efecto para leer el parámetro de URL y seleccionar el producto
    useEffect(() => {
        const selectedParam = searchParams.get('selected');
        if (selectedParam !== null) {
            const productIndex = parseInt(selectedParam, 10);
            if (productIndex >= 0 && productIndex < productData.length) {
                setSelectedProduct(productIndex);
            }
        }
    }, [searchParams]);

    const handleProductSelect = (index) => {
        setSelectedProduct(index);
        setIsMobileMenuOpen(false); // Cierra el menú móvil al seleccionar un producto
        // Actualizar la URL con el producto seleccionado
        setSearchParams({ selected: index.toString() });
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const toggleCategory = (categoryName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    };

    const openModal = (imageSrc) => {
        setModalImage(imageSrc);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalImage(null);
    };

    const openWhatsApp = (productName) => {
        const message = encodeURIComponent(`${t('whatsappMessage')} ${productName}`);
        const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
        window.open(whatsappUrl, '_blank');
    };

    const getProductImages = (productIndex) => {
        const productImageData = productImages[productIndex];
        if (!productImageData) return [];
        
        const images = [];
        if (productImageData.main) {
            images.push({ src: productImageData.main, label: t('mainImage') });
        }
        if (productImageData.seed) {
            images.push({ src: productImageData.seed, label: t('seedImage') });
        }
        if (productImageData.growth && productImageData.growth !== productImageData.main) {
            images.push({ src: productImageData.growth, label: t('growthImage') });
        }
        
        return images;
    };

    return (
        <Container>
            <MobileOverlay isOpen={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(false)} />
            
            <Sidebar isOpen={isMobileMenuOpen}>
                <SidebarTitle>{t('product_title')}</SidebarTitle>
                {Object.entries(groupedProducts).map(([categoryName, products]) => {
                    const config = categoryConfig[categoryName];
                    const IconComponent = config?.icon || FaSeedling;
                    const isExpanded = expandedCategories[categoryName];
                    
                    return (
                        <div key={categoryName}>
                            <CategoryHeader
                                isExpanded={isExpanded}
                                categoryColor={config?.color}
                                onClick={() => toggleCategory(categoryName)}
                            >
                                <CategoryTitle>
                                    <CategoryIcon categoryColor={config?.color}>
                                        <IconComponent />
                                    </CategoryIcon>
                                    {categoryName}
                                </CategoryTitle>
                                <CategoryChevron isExpanded={isExpanded}>
                                    <FaChevronDown />
                                </CategoryChevron>
                            </CategoryHeader>
                            <CategoryContent isExpanded={isExpanded}>
                                <CategoryProductList>
                                    {products.map((product) => (
                                        <ProductListItem
                                            key={product.originalIndex}
                                            isActive={selectedProduct === product.originalIndex}
                                            onClick={() => handleProductSelect(product.originalIndex)}
                                        >
                                            <ProductName>{t(`product_data.${product.originalIndex}.title`)}</ProductName>
                                            <ProductType>{t(`product_data.${product.originalIndex}.type`)}</ProductType>
                                        </ProductListItem>
                                    ))}
                                </CategoryProductList>
                            </CategoryContent>
                        </div>
                    );
                })}
            </Sidebar>
            
            <DetailArea>
                {selectedProduct !== null ? (
                    <ProductDetailContent>
                        <ImageColumn>
                            <ImageContainer isSorgo={t(`product_data.${selectedProduct}.type`) === 'Sorgos' || 
                                                   t(`product_data.${selectedProduct}.type`) === 'Sorghums' || 
                                                   t(`product_data.${selectedProduct}.type`) === '高粱'}>
                                {(() => {
                                    const productImageList = getProductImages(selectedProduct);
                                    const isSorgo = t(`product_data.${selectedProduct}.type`) === 'Sorgos' || 
                                                   t(`product_data.${selectedProduct}.type`) === 'Sorghums' || 
                                                   t(`product_data.${selectedProduct}.type`) === '高粱';
                                    
                                    if (productImageList.length === 0) {
                                        return (
                                            <ProductImage 
                                                src={generatePlaceholderImage(t(`product_data.${selectedProduct}.type`), t(`product_data.${selectedProduct}.title`))}
                                                alt={t(`product_data.${selectedProduct}.title`)}
                                                onClick={() => openModal(generatePlaceholderImage(t(`product_data.${selectedProduct}.type`), t(`product_data.${selectedProduct}.title`)))}
                                                isSorgo={isSorgo}
                                            />
                                        );
                                    }
                                    
                                    return productImageList.map((imageData, index) => (
                                        <div key={index}>
                                            <ImageLabel>{imageData.label}</ImageLabel>
                                            <ProductImage 
                                                src={imageData.src}
                                                alt={`${t(`product_data.${selectedProduct}.title`)} - ${imageData.label}`}
                                                onClick={() => openModal(imageData.src)}
                                                isSorgo={isSorgo}
                                            />
                                        </div>
                                    ));
                                })()}
                            </ImageContainer>
                        </ImageColumn>
                        <ContentColumn>
                            <ProductTitle>{t(`product_data.${selectedProduct}.title`)}</ProductTitle>
                            <div style={{ marginBottom: '1rem' }}>
                                <ProductTypeTag>{t(`product_data.${selectedProduct}.type`)}</ProductTypeTag>
                            </div>
                            <ConsultPriceButton
                                onClick={() => openWhatsApp(t(`product_data.${selectedProduct}.title`))}
                                whileHover={{ scale: 1.006 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                </svg>
                                {t('consultPrice')}
                            </ConsultPriceButton>
                            
                            {/* Mostrar especificaciones técnicas si existen */}
                            {(t(`product_data.${selectedProduct}.cycle`, { defaultValue: '' }) || 
                              t(`product_data.${selectedProduct}.floweringDays`, { defaultValue: '' }) || 
                              t(`product_data.${selectedProduct}.averageHeight`, { defaultValue: '' }) || 
                              t(`product_data.${selectedProduct}.tanninContent`, { defaultValue: '' }) || 
                              t(`product_data.${selectedProduct}.grainColor`, { defaultValue: '' }) || 
                              t(`product_data.${selectedProduct}.aphidTolerance`, { defaultValue: '' }) ||
                              t(`product_data.${selectedProduct}.biotipo`, { defaultValue: '' }) ||
                              t(`product_data.${selectedProduct}.characteristics`, { defaultValue: '' }) ||
                              t(`product_data.${selectedProduct}.composition`, { defaultValue: '' }) ||
                              t(`product_data.${selectedProduct}.coverage`, { defaultValue: '' })) && (
                                <SpecificationsContainer>
                                    <SpecificationsTitle>{t('technicalSpecifications')}</SpecificationsTitle>
                                    {t(`product_data.${selectedProduct}.cycle`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('cycle')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.cycle`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.floweringDays`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('floweringDays')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.floweringDays`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.averageHeight`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('averageHeight')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.averageHeight`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.tanninContent`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('tanninContent')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.tanninContent`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.grainColor`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('grainColor')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.grainColor`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.aphidTolerance`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('aphidTolerance')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.aphidTolerance`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.biotipo`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('biotipo')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.biotipo`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.characteristics`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('characteristics')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.characteristics`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.composition`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('composition')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.composition`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                    {t(`product_data.${selectedProduct}.coverage`, { defaultValue: '' }) && (
                                        <SpecificationItem>
                                            <SpecLabel>{t('coverage')}</SpecLabel>
                                            <SpecValue>{t(`product_data.${selectedProduct}.coverage`)}</SpecValue>
                                        </SpecificationItem>
                                    )}
                                </SpecificationsContainer>
                            )}
                            
                            <FeaturesTitle>{t('mainFeatures')}</FeaturesTitle>
                            <FeaturesList>
                                {t(`product_data.${selectedProduct}.features`, { returnObjects: true }).map((feature, index) => (
                                    <FeatureItem key={index}>{feature}</FeatureItem>
                                ))}
                            </FeaturesList>
                        </ContentColumn>
                    </ProductDetailContent>
                ) : (
                    <WelcomeMessage>
                        <h2>{t('selectProductMessage')}</h2>
                        <p>{t('selectProductSubMessage')}</p>
                    </WelcomeMessage>
                )}
            </DetailArea>
            
            {/* Modal para ver imágenes en grande */}
            <ImageModal isOpen={isModalOpen} onClick={closeModal}>
                <ModalCloseButton onClick={closeModal}>×</ModalCloseButton>
                {modalImage && (
                    <ModalImage 
                        src={modalImage} 
                        alt="Imagen ampliada"
                        onClick={(e) => e.stopPropagation()}
                    />
                )}
            </ImageModal>
        </Container>
    );
};

export default ProductDetail;
